import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, message, Popconfirm, Input } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CreateCategoryModal } from '../CreateCategoryModal';
import EditCategoryModal from '../EditCategoryModal';
import apiService from '@/services/api.service';
import type { Category } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const CategoriesPage: React.FC = () => {
    const location = useLocation();
    const [categories, setCategories] = useState<Category[]>([]);
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
    const [isCreateCategoryModalVisible, setIsCreateCategoryModalVisible] = useState(false);
    const [isEditCategoryModalVisible, setIsEditCategoryModalVisible] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

    const fetchCategories = async () => {
        try {
            setIsCategoriesLoading(true);
            const response = await apiService.get<ApiResponse<Category[]>>(
                API_ENDPOINTS.CATEGORIES.BASE
            );
            const categoriesData = response.data.data.map(category => ({
                ...category,
                createdAt: category.createdAt || new Date().toISOString(),
                updatedAt: category.updatedAt || new Date().toISOString()
            }));
            setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } catch (error) {
            logger.error('Error fetching categories:', error);
            message.error('Ошибка при загрузке списка категорий');
            setCategories([]);
        } finally {
            setIsCategoriesLoading(false);
        }
    };

    const handleCategoryCreateSuccess = () => {
        setIsCreateCategoryModalVisible(false);
        setSelectedCategory(null);
        fetchCategories();
    };

    const handleCategoryEditSuccess = () => {
        setIsEditCategoryModalVisible(false);
        setSelectedCategory(null);
        fetchCategories();
    };

    const handleDeleteCategory = async (id: number) => {
        try {
            await apiService.delete(`${API_ENDPOINTS.CATEGORIES.BASE}/${id}`);
            message.success('Категория успешно удалена');
            fetchCategories();
        } catch (error) {
            logger.error('Error deleting category:', error);
            message.error('Ошибка при удалении категории');
        }
    };

    useEffect(() => {
        if (location.pathname === '/admin/categories') {
            fetchCategories();
        }
    }, [location.pathname]);

    const categoryColumns: ColumnsType<Category> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: Category, b: Category) => a.name.localeCompare(b.name),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по названию"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Вес',
            key: 'weight',
            render: (_: unknown, record: Category) => {
                const weightTo = record.weightTo === 999 ? '+' : record.weightTo;
                return `${record.weightFrom}-${weightTo} кг`;
            },
            sorter: (a, b) => a.weightFrom - b.weightFrom,
        },
        {
            title: 'Возраст',
            key: 'age',
            render: (_: unknown, record: Category) =>
                `${record.ageFrom}-${record.ageTo} лет`,
            sorter: (a, b) => a.ageFrom - b.ageFrom,
        },
        {
            title: 'Пол',
            dataIndex: 'gender',
            key: 'gender',
            filters: [
                { text: 'Мужской', value: 'male' },
                { text: 'Женский', value: 'female' },
                { text: 'Смешанный', value: 'mixed' },
            ],
            onFilter: (value, record) => record.gender === value,
            render: (gender: 'male' | 'female' | 'mixed') => {
                const genderMap = {
                    male: 'Мужской',
                    female: 'Женский',
                    mixed: 'Смешанный'
                };
                return genderMap[gender];
            },
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: Category) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedCategory(record);
                        setIsEditCategoryModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить категорию?"
                        description="Это действие нельзя отменить"
                        onConfirm={() => handleDeleteCategory(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedCategory(null);
                            setIsCreateCategoryModalVisible(true);
                        }}
                    >
                        Добавить категорию
                    </Button>
                    <Button
                        icon={<UploadOutlined />}
                        onClick={() => {/* TODO: Implement import */}}
                    >
                        Импорт
                    </Button>
                </Space>
            </div>
            <Table
                columns={categoryColumns}
                dataSource={categories}
                rowKey="id"
                loading={isCategoriesLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} категорий`,
                }}
            />
            <CreateCategoryModal
                visible={isCreateCategoryModalVisible}
                category={selectedCategory}
                onCancel={() => {
                    setIsCreateCategoryModalVisible(false);
                    setSelectedCategory(null);
                }}
                onSuccess={handleCategoryCreateSuccess}
            />
            <EditCategoryModal
                visible={isEditCategoryModalVisible}
                category={selectedCategory}
                onCancel={() => {
                    setIsEditCategoryModalVisible(false);
                    setSelectedCategory(null);
                }}
                onSuccess={handleCategoryEditSuccess}
            />
        </div>
    );
};

export default CategoriesPage;


